import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Mail, Sparkles, Target } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function CoverLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>AI Cover Letter Generator - Personalized Cover Letters | LoopBaan.ai</title>
        <meta name="description" content="Generate personalized, compelling cover letters in seconds with AI. Tailored to each job application." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-accent/10 via-background to-primary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-accent/20 text-accent border-accent/30">AI-POWERED</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Cover Letters That{" "}
                <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                  Get Responses
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                AI writes personalized cover letters tailored to each job. Stand out from generic templates with compelling, custom content.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Generate Cover Letter <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/samples">
                  <Button size="lg" variant="outline">View Examples</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: Sparkles,
                  title: "Job-Specific Content",
                  description: "AI analyzes the job description and tailors your cover letter to match what the employer is looking for."
                },
                {
                  icon: Target,
                  title: "Achievement-Focused",
                  description: "Highlight your most relevant accomplishments that demonstrate you're the perfect fit for the role."
                },
                {
                  icon: Mail,
                  title: "Professional Tone",
                  description: "Choose from different writing styles: professional, enthusiastic, confident, or conversational."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">What Makes It Different?</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Personalized to each job posting",
                  "Incorporates your unique experience",
                  "Matches company culture and values",
                  "Highlights relevant achievements",
                  "Professional formatting",
                  "Multiple tone options",
                  "Edit and refine with AI",
                  "Save as templates for similar roles"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Stop Using Generic Templates
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Let AI write personalized cover letters that get you noticed by hiring managers.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Create Your Cover Letter <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
